
import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { X, ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useNavigate } from "react-router-dom";
import { Story } from "@/services/storiesService";
import { profileRouterService } from "@/services/profileRouterService";

interface EnhancedStoryViewerProps {
  stories: Story[];
  currentStoryIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onStoryChange: (index: number) => void;
}

const EnhancedStoryViewer = ({ 
  stories, 
  currentStoryIndex, 
  isOpen, 
  onClose, 
  onStoryChange 
}: EnhancedStoryViewerProps) => {
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const navigate = useNavigate();
  const currentStory = stories[currentStoryIndex];

  useEffect(() => {
    if (!isOpen || !currentStory || isPaused) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          if (currentStoryIndex < stories.length - 1) {
            onStoryChange(currentStoryIndex + 1);
            return 0;
          } else {
            onClose();
            return 0;
          }
        }
        return prev + (100 / (currentStory.duration * 10)); // Progress based on actual duration
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentStory, currentStoryIndex, isOpen, onClose, onStoryChange, stories.length, isPaused]);

  useEffect(() => {
    setProgress(0);
  }, [currentStoryIndex]);

  const handlePrevious = () => {
    if (currentStoryIndex > 0) {
      onStoryChange(currentStoryIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentStoryIndex < stories.length - 1) {
      onStoryChange(currentStoryIndex + 1);
    } else {
      onClose();
    }
  };

  const handleAvatarClick = () => {
    navigate(profileRouterService.getProfileRoute(currentStory.user_id, currentStory.user_name));
    onClose();
  };

  const handleHold = () => {
    setIsPaused(true);
  };

  const handleRelease = () => {
    setIsPaused(false);
  };

  if (!currentStory) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm mx-auto h-[85vh] p-0 bg-black border-none" style={{ aspectRatio: '9/16' }}>
        <div className="relative h-full w-full">
          {/* Progress bars */}
          <div className="absolute top-2 left-2 right-2 z-10 flex space-x-1">
            {stories.map((_, index) => (
              <div key={index} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-electric-violet transition-all duration-100"
                  style={{ 
                    width: index < currentStoryIndex ? '100%' : 
                           index === currentStoryIndex ? `${progress}%` : '0%'
                  }}
                />
              </div>
            ))}
          </div>

          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 z-10 text-white hover:bg-white/20"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>

          {/* User info with clickable avatar */}
          <div className="absolute top-12 left-4 right-4 z-10 flex items-center space-x-3">
            <button onClick={handleAvatarClick} className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <Avatar className="w-9 h-9 border-2 border-white ring-2 ring-electric-violet">
                <AvatarImage src={currentStory.user_avatar} />
                <AvatarFallback className="bg-electric-violet text-white">
                  {currentStory.user_name?.[0]?.toUpperCase() || '?'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-white font-semibold text-sm">{currentStory.user_name}</p>
                <p className="text-white/80 text-xs">
                  {currentStory.distance_km ? `${currentStory.distance_km.toFixed(1)}km away` : 'Nearby'}
                </p>
              </div>
            </button>
          </div>

          {/* Sound toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-12 right-4 z-10 text-white hover:bg-white/20"
            onClick={() => setIsMuted(!isMuted)}
          >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </Button>

          {/* Story video */}
          <div className="w-full h-full">
            <video
              src={currentStory.media_url}
              className="w-full h-full object-cover"
              autoPlay
              muted={isMuted}
              loop={false}
              playsInline
            />
          </div>

          {/* Navigation areas */}
          <div className="absolute inset-0 flex">
            <div 
              className="w-1/2 h-full cursor-pointer" 
              onClick={handlePrevious}
              onTouchStart={handleHold}
              onTouchEnd={handleRelease}
              onMouseDown={handleHold}
              onMouseUp={handleRelease}
              onMouseLeave={handleRelease}
            />
            <div 
              className="w-1/2 h-full cursor-pointer" 
              onClick={handleNext}
              onTouchStart={handleHold}
              onTouchEnd={handleRelease}
              onMouseDown={handleHold}
              onMouseUp={handleRelease}
              onMouseLeave={handleRelease}
            />
          </div>

          {/* Navigation buttons */}
          {currentStoryIndex > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white hover:bg-white/20"
              onClick={handlePrevious}
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
          )}

          {currentStoryIndex < stories.length - 1 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white hover:bg-white/20"
              onClick={handleNext}
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          )}

          {/* Pause indicator */}
          {isPaused && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-black/50 rounded-full p-4">
                <div className="text-white text-sm">Hold to pause</div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedStoryViewer;
